import { Component, OnInit, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  FormsModule,
  Validators,
} from "@angular/forms";
import { InputTextModule } from "primeng/inputtext";
import { TextareaModule } from "primeng/textarea";
import { SelectModule } from "primeng/select";
import { ButtonModule } from "primeng/button";
import { MenuModule } from "primeng/menu";
import { TooltipModule } from "primeng/tooltip";
import { ConfirmService } from "../../../Utils/_services/confirmation-dialog.service";
import { ToastrService } from "ngx-toastr";
import { MenuItem } from "primeng/api";
import { NotificationService } from "../../../Utils/_services/admin/notifications/notification.service";
import { COUNTRIES } from "../../../Utils/constants/countries";
import {
  IAdminNotificationListItem,
  INotificationListReq,
  ISendNotificationReq,
} from "../../../Utils/_models/notification.model";
import { SearchBoxComponent } from "../../../shared/components/search-box/search-box.component";
import { SpinnerLoaderComponent } from "../../../shared/components/spinner-loader/spinner-loader.component";
import { CreateNotificationModalComponent } from "./create-notification-modal/create-notification-modal.component";

@Component({
  selector: "app-notification-management",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SelectModule,
    ButtonModule,
    MenuModule,
    TooltipModule,
    SearchBoxComponent,
    SpinnerLoaderComponent,
    CreateNotificationModalComponent,
  ],
  templateUrl: "./notification-management.component.html",
  styleUrls: ["./notification-management.component.css"],
})
export class NotificationManagementComponent implements OnInit {
  loading = false;
  showCreateModal = false;

  filterAudienceOptions = [
    { label: "All Audiences", value: "" },
    { label: "All Users", value: "all" },
    { label: "Free Users", value: "free" },
    { label: "Premium Users", value: "premium" },
    { label: "Inactive Users", value: "inactive" },
    { label: "Early Release Users", value: "early_release" },
    { label: "Admin Users", value: "admin" },
    { label: "Single User", value: "single" },
  ];

  filterDeliveryTypeOptions = [
    { label: "All Delivery Types", value: "" },
    { label: "Push Notification", value: "push" },
    { label: "Email", value: "email" },
    { label: "SMS", value: "sms", disabled: true },
    { label: "Voice Call", value: "voice" },
  ];

  filterStatusOptions = [
    { label: "All Statuses", value: "" },
    { label: "Pending", value: "pending" },
    { label: "Processing", value: "processing" },
    { label: "Completed", value: "completed" },
    { label: "Failed", value: "failed" },
  ];

  searchQuery = "";
  selectedFilterAudience = "";
  selectedFilterDeliveryType = "";
  selectedFilterStatus = "";
  showFiltersDropdown = false;

  // View detail modal properties
  showViewModal = false;
  selectedNotification: IAdminNotificationListItem | null = null;

  allNotifications: IAdminNotificationListItem[] = [];

  pageSize = 5;
  currentPage = 1;
  pageSizes: number[] = [5, 10, 20, 50];
  totalCount = 0;

  includeDeleted = signal(false);
  selectedIds = new Set<string>();
  hasSelection = signal(false);
  hasDeletedSelection = signal(false);
  allSelected = signal(false);
  currentSortField = signal<string | null>(null);
  currentSortOrder = signal<"asc" | "desc" | null>(null);
  originalNotificationList = signal<IAdminNotificationListItem[]>([]);
  toggleIncludeDeleted(): void {
    this.includeDeleted.set(!this.includeDeleted());
    this.fetchNotifications();
  }

  updateSelectionState(): void {
    const all = this.allNotifications;
    this.hasSelection.set(this.selectedIds.size > 0);
    this.allSelected.set(
      all.length > 0 && all.every(n => n._id && this.selectedIds.has(n._id))
    );
    this.hasDeletedSelection.set(
      all.some(n => n._id && this.selectedIds.has(n._id) && !!n.deletedAt)
    );
  }

  isSelected(notif: any): boolean {
    return this.selectedIds.has(notif._id);
  }

  onToggleSelectAll(): void {
    const currentState = this.allSelected();
    this.allSelected.set(!currentState);

    if (!currentState) {
      this.allNotifications.forEach(n => {
        if (n._id) this.selectedIds.add(n._id);
      });
    } else {
      this.selectedIds.clear();
    }
    this.updateSelectionState();
  }

  onToggleSelect(notif: any): void {
    if (this.selectedIds.has(notif._id)) {
      this.selectedIds.delete(notif._id);
    } else {
      this.selectedIds.add(notif._id);
    }
    this.updateSelectionState();
  }

  async deleteSelectedNotifications(): Promise<void> {
    if (this.selectedIds.size === 0) return;

    const all = this.allNotifications;
    const selectedNotifs = all.filter(
      n => n._id && this.selectedIds.has(n._id)
    );
    const deletedSelected = selectedNotifs.filter(n => n.deletedAt);
    const activeSelected = selectedNotifs.filter(n => !n.deletedAt);
    const isRestore = deletedSelected.length > 0;

    const header = isRestore ? "Restore Notifications" : "Delete Notifications";
    const bodyHeader = isRestore
      ? "Restore Selected Notifications"
      : "Delete Selected Notifications";
    const bodySubtitle = isRestore
      ? `You are about to restore ${deletedSelected.length} deleted notification(s).${activeSelected.length > 0 ? ` ${activeSelected.length} non-deleted notification(s) will also be deleted.` : ""}`
      : `Are you sure you want to delete ${activeSelected.length} selected notification(s)? This action cannot be undone.`;
    const yesButtonText =
      deletedSelected.length > 0 && activeSelected.length > 0
        ? "Apply Changes"
        : isRestore
          ? "Restore"
          : "Delete";
    const yesBtnCss = isRestore ? "btn-primary" : "btn-danger";

    const confirmed = await this.confirmationService.confirm({
      header,
      bodyHeader,
      bodySubtitle,
      yesButtonText,
      noButtonText: "Cancel",
      yesBtnCss,
      noBtnCss: "btn-secondary",
      width: "420px",
    });

    if (confirmed) {
      const ids = Array.from(this.selectedIds);
      this.notificationService.bulkDelete(ids).subscribe({
        next: res => {
          if (res.auth?.success) {
            this.toastr.success(
              res.msg || "Notifications deleted successfully"
            );
            this.selectedIds.clear();
            this.updateSelectionState();
            this.fetchNotifications();
          } else {
            this.toastr.error(res.msg || "Failed to delete notifications");
          }
        },
        error: err => {
          this.toastr.error(
            err.error?.msg || err.message || "Failed to delete notifications"
          );
        },
      });
    }
  }

  toggleFilters() {
    this.showFiltersDropdown = !this.showFiltersDropdown;
    if (!this.showFiltersDropdown) {
      this.selectedFilterAudience = ""; // reset filter on hide
      this.selectedFilterDeliveryType = ""; // reset filter on hide
      this.selectedFilterStatus = ""; // reset filter on hide
      this.onSearch();
    }
  }

  onSearch(term?: string) {
    if (term !== undefined) {
      this.searchQuery = term;
    }
    this.currentPage = 1;
    this.fetchNotifications();
  }

  get totalPages() {
    return Math.max(1, Math.ceil(this.totalCount / this.pageSize));
  }
  get showingFrom() {
    return this.totalCount === 0
      ? 0
      : (this.currentPage - 1) * this.pageSize + 1;
  }
  get showingTo() {
    return Math.min(this.currentPage * this.pageSize, this.totalCount);
  }

  actionMenuItems: MenuItem[] = [];
  activeNotification: IAdminNotificationListItem | null = null;

  constructor(
    private confirmationService: ConfirmService,
    private toastr: ToastrService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.actionMenuItems = [
      {
        label: "View",
        icon: "pi pi-eye",
        command: () => this.viewNotification(),
      },
      {
        label: "Resend",
        icon: "pi pi-send",
        command: () => this.resendNotification(),
      },
      {
        label: "Delete",
        icon: "pi pi-trash",
        styleClass: "menu-danger",
        // command: () => this.deleteNotification(),
      },
    ];

    this.fetchNotifications();
  }

  fetchNotifications() {
    this.loading = true;
    const req: INotificationListReq = {
      page: this.currentPage,
      limit: this.pageSize,
      search: this.searchQuery || null,
      audience: this.selectedFilterAudience || null,
      deliveryType: this.selectedFilterDeliveryType || null,
      status: this.selectedFilterStatus || null,
      includeDeleted: this.includeDeleted(),
    };

    this.notificationService.getAll(req).subscribe({
      next: res => {
        const all = res.data.notifications;
        this.allNotifications = all;
        this.originalNotificationList.set([...all])
        this.totalCount = res.data.totalCount;
         if (this.currentSortField() && this.currentSortOrder()) {
            this.sortNotificationListLocal();
          } 
        this.loading = false;
      },
      error: err => {
        this.toastr.error(err.message || "Failed to fetch notifications");
        this.loading = false;
      },
    });
  }

  onNotificationSent(): void {
    this.showCreateModal = false;
    this.currentPage = 1;
    this.fetchNotifications();
  }

  getAudienceClass(audience: string): string {
    const audienceClassMap: Record<string, string> = {
      all: "audience-all",
      free: "audience-free",
      premium: "audience-premium",
      inactive: "audience-inactive",
      early_release: "audience-early",
      admin: "audience-admin",
      single: "audience-single",
    };
    return audienceClassMap[audience] ?? "audience-all";
  }

  getAudienceLabel(audience: string): string {
    const audienceLabelMap: Record<string, string> = {
      all: "All Users",
      free: "Free Users",
      premium: "Premium Users",
      inactive: "Inactive Users",
      early_release: "Early Release Users",
      admin: "Admin Users",
      single: "Single User",
    };
    return audienceLabelMap[audience] ?? audience;
  }

  openMenu(notif: IAdminNotificationListItem) {
    this.activeNotification = notif;
  }

  viewNotification(notif?: IAdminNotificationListItem): void {
    if (notif) {
      this.selectedNotification = notif;
      this.activeNotification = notif;
    } else if (this.activeNotification) {
      this.selectedNotification = this.activeNotification;
    }
    this.showViewModal = true;
  }

  resendNotification(): void {
    if (this.activeNotification) {
      this.loading = true;
      this.notificationService.resend(this.activeNotification._id).subscribe({
        next: res => {
          if (res.auth?.success) {
            this.currentPage = 1;
            this.fetchNotifications();
            this.closeViewModal();
            this.toastr.success(res.msg || "Notification resent successfully");
          } else {
            this.toastr.error(res.msg || "Failed to resend notification");
            this.loading = false;
          }
        },
        error: err => {
          this.toastr.error(
            err.error?.msg || err.message || "Failed to resend notification"
          );
          this.loading = false;
        },
      });
    }
  }

  prevPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.fetchNotifications();
    }
  }
  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.fetchNotifications();
    }
  }

  onPageSizeChange(event: Event) {
    const target = event.target as HTMLSelectElement;
    this.pageSize = +target.value;
    this.currentPage = 1; // reset to first page
    this.fetchNotifications();
  }

  closeViewModal() {
    this.showViewModal = false;
    this.selectedNotification = null;
  }
onSort(field: string): void {
    if (this.currentSortField() === field) {
      if (this.currentSortOrder() === "asc") {
        this.currentSortOrder.set("desc");
      } else if (this.currentSortOrder() === "desc") {
        this.currentSortOrder.set(null);
        this.currentSortField.set(null);
      }
    } else {
      this.currentSortField.set(field);
      this.currentSortOrder.set("asc");
    }
    this.sortNotificationListLocal();
  }

  sortNotificationListLocal(): void {
    const field = this.currentSortField();
    const order = this.currentSortOrder();

    if (!field || !order) {
      this.allNotifications = [...this.originalNotificationList()];

      return;
    }

    const direction = order === "asc" ? 1 : -1;
    const sorted = [...this.allNotifications];

    sorted.sort((a: any, b: any) => {
      let valA: any = a[field];
      let valB: any = b[field];

      if (field === 'title') {
        valA = (a.title || '').toLowerCase();
        valB = (b.title || '').toLowerCase();
      } else if (field === 'createdAt') {
        valA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        valB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      } else if (field === 'deliveryType') {
        valA = (a.deliveryType || '').toLowerCase();
        valB = (b.deliveryType || '').toLowerCase();
      } else if (field === 'audience') {
        valA = (a.audience || '').toLowerCase();
        valB = (b.audience || '').toLowerCase();
      } 

      if (valA < valB) return -1 * direction;
      if (valA > valB) return 1 * direction;
      return 0;
    });

    this.allNotifications = sorted;

  }

  getSortIcon(field: string): string {
    if (this.currentSortField() !== field) return "⇅";
    if (this.currentSortOrder() === "asc") return "↑";
    if (this.currentSortOrder() === "desc") return "↓";
    return "⇅";
  }
}